import { Alert, Button, Collapse, PasswordInput, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCheckEmailStatus } from 'src/Hooks/Mutations/useCheckEmailStatus';
import { SignInError, useSignIn } from 'src/Hooks/Mutations/useSignIn';
import { EmailStatusDtoLoginMethodEnum } from 'src/Services/API/generated';

import styles from './SignInForm.module.scss';

type EmailCheckStep = 'email_input' | 'password_input' | 'sso_only';

const SSO_PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

const SignInForm = () => {
  const [emailCheckStep, setEmailCheckStep] = useState<EmailCheckStep>('email_input');
  const [ssoProvider, setSsoProvider] = useState<string | undefined>();

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value: string) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value: string) =>
        emailCheckStep === 'password_input' && value.length < 2 ? 'Please enter password' : null,
    },
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl') ?? '/';

  const onSignInError = useCallback(
    (error: unknown) => {
      if (error instanceof SignInError) {
        const formFields = error.getDetails();
        const values: Record<string, string> = {};
        formFields.forEach(ff => (values[ff.id] = ff.error));
        form.setErrors(values);
      } else {
        alert('Unknown error, please check your network');
      }
    },
    [form]
  );

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { mutate: signIn } = useSignIn({
    onError: onSignInError,
    onSuccess: () => navigate(returnUrl),
  });

  const { mutate: checkEmailStatus, isLoading: isCheckingEmail } = useCheckEmailStatus({
    onSuccess: data => {
      if (data.loginMethod === EmailStatusDtoLoginMethodEnum.EmailPassword) {
        setEmailCheckStep('password_input');
      } else if (data.loginMethod === EmailStatusDtoLoginMethodEnum.NotFound) {
        navigate(`/auth/register?email=${encodeURIComponent(form.values.email)}`);
      } else if (data.loginMethod === EmailStatusDtoLoginMethodEnum.SsoOnly) {
        setSsoProvider(data.ssoProvider);
        setEmailCheckStep('sso_only');
      }
    },
  });

  const handleSubmit = (values: { email: string; password: string }) => {
    if (emailCheckStep === 'email_input') {
      checkEmailStatus({ email: values.email });
    } else if (emailCheckStep === 'password_input') {
      signIn(values);
    }
  };

  const handleEditEmail = () => {
    setEmailCheckStep('email_input');
    form.setFieldValue('password', '');
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
      <div className={styles.subcontainer}>
        <p className={styles.welcome}>{t('signinForm.welcome', 'Bienvenue chez vous')}</p>

        <TextInput
          placeholder={t('signinForm.typeEmail', 'Entrez votre adresse email')}
          description={t('signinForm.emailDescription', 'Email')}
          type="email"
          disabled={emailCheckStep !== 'email_input'}
          {...form.getInputProps('email')}
        />

        {emailCheckStep !== 'email_input' && (
          <a className={styles.editEmail} onClick={handleEditEmail}>
            {t('signinForm.editEmail', 'Modifier l\u2019email')}
          </a>
        )}

        {emailCheckStep === 'sso_only' && (
          <Alert color="blue">
            {ssoProvider
              ? t('signinForm.ssoOnlyWithProvider', {
                  defaultValue:
                    'Ce compte est lié à {{provider}}. Connectez-vous avec le bouton {{provider}} ci-dessous.',
                  provider: SSO_PROVIDER_LABELS[ssoProvider] ?? ssoProvider,
                })
              : t(
                  'signinForm.ssoOnly',
                  'Ce compte est lié à Google, Facebook ou Apple. Connectez-vous avec le bouton correspondant ci-dessous.'
                )}
          </Alert>
        )}

        <Collapse in={emailCheckStep === 'password_input'}>
          <div className={styles.passwordSection}>
            <PasswordInput
              placeholder="············"
              description={t('signinForm.passwordDescription', 'Mot de passe')}
              {...form.getInputProps('password')}
            />

            <div className={styles.passwordResetRow}>
              <a onClick={() => navigate('/auth/reset-password')}>
                {t('signinForm.resetPassword', 'Mot de passe oublié ?')}
              </a>
            </div>
          </div>
        </Collapse>

        <Button
          variant="filled"
          type="submit"
          loading={isCheckingEmail}
          disabled={emailCheckStep === 'sso_only'}
        >
          {emailCheckStep === 'password_input'
            ? t('signinForm.signin', 'Connexion')
            : t('signinForm.next', 'Suivant')}
        </Button>
      </div>
    </form>
  );
};

export default SignInForm;
